import { DeepPartial } from '../util/types';
import z, { boolean as Boolean, object as Object, string as String } from 'zod';

// Hybrid is a combination of Cognito (Amplify) and Apigee.
const AuthType = z.literal('Hybrid');

const CognitoAuthSchema = Object({
  region: String(),
  userPoolId: String(),
  userPoolWebClientId: String(),
  authenticationFlowType: String(),
});

const ConfigSchema = Object({
  env: String(),
  googleTagManager: Object({
    oPowerWidgetCode: String(),
  }),
  storageKey: String(),
  graphql: Object({
    baseUrl: String(),
    baseUrlPgePlus: String(),
    baseUrlLegacy: String().optional(),
  }),
  apigee: Object({
    baseUrl: String(),
    auth: Object({
      url: String(),
      urlLegacy: String().optional(),
      clientId: String(),
    }),
    widgetAuth: Object({
      clientId: String(),
    }),
  }),
  edgeApi: Object({
    baseUrl: String(),
    baseUrlLegacy: String(),
  }),
  apm: Object({
    active: Boolean(),
    serverUrl: String(),
  }),
  auth: Object({
    type: AuthType,
  }),
  contentful: Object({
    host: String(),
    spaceId: String(),
    environmentName: String(),
    accessToken: String(),
  }),
  firebase: Object({
    key: String(),
    databaseURL: String(),
    projectId: String(),
    appId: String(),
  }),
  cognito: CognitoAuthSchema.extend({
    installerPortal: CognitoAuthSchema,
    invoicePortal: CognitoAuthSchema,
  }),
  legacySiteURL: String(),
  bizWebLegacyURL: String(),
  bizApiLegacyURL: String(),
  csApiLegacyURL: String(),
  salseforceEVFormURL: String(),
  amazonLocationService: Object({
    key: String(),
    region: String(),
    arcgisPSPSURL: String(),
  }),
});

type Config = z.infer<typeof ConfigSchema>;

const APIGEE_GENERIC_PATH = '/pge-graphql';

const baseCognito = {
  region: process.env.GATSBY_COGNITO_REGION,
  userPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
  userPoolWebClientId: process.env.GATSBY_COGNITO_USER_POOL_WEB_CLIENT_ID,
  authenticationFlowType: process.env.GATSBY_COGNITO_AUTH_FLOW_TYPE,
};

const rawConfig: DeepPartial<Config> = {
  env: process.env.GATSBY_ACTIVE_ENV,
  auth: {
    type:
      (process.env.GATSBY_AUTH_TYPE as z.infer<typeof AuthType>) || 'Hybrid',
  },
  googleTagManager: {
    oPowerWidgetCode: process.env.GATSBY_GTM_WORKSPACE_ID,
  },
  storageKey: 'p2s5u8x/A?D(G+KbPeShVmYq3t6w9y$B',
  graphql: {
    baseUrl: `${process.env.GATSBY_APIGEE_URL}`,
    baseUrlPgePlus: `${process.env.GATSBY_APIGEE_URL_PGEPLUS}`,
    baseUrlLegacy: process.env.GATSBY_APIGEE_URL_LEGACY,
  },
  apigee: {
    baseUrl: process.env.GATSBY_APIGEE_URL,
    auth: {
      url: process.env.GATSBY_APIGEE_URL?.split(APIGEE_GENERIC_PATH)?.[0],
      urlLegacy: process.env.GATSBY_APIGEE_URL_LEGACY?.split(
        APIGEE_GENERIC_PATH,
      )?.[0],
      clientId: process.env.GATSBY_APIGEE_AUTH_CLIENT_ID,
    },
    widgetAuth: {
      clientId: process.env.GATSBY_APIGEE_WIDGETAUTH_CLIENT_ID,
    },
  },
  edgeApi: {
    baseUrl: process.env.GATSBY_EDGE_API_URL,
    baseUrlLegacy: process.env.GATSBY_EDGE_API_URL_LEGACY,
  },
  apm: {
    active: process.env.GATSBY_APM_ACTIVE === 'true',
    serverUrl: process.env.GATSBY_APM_URL,
  },
  contentful: {
    host: process.env.GATSBY_REALTIME_CONTENTFUL_HOST,
    spaceId: process.env.GATSBY_REALTIME_CONTENTFUL_SPACE_ID,
    environmentName: process.env.GATSBY_REALTIME_CONTENTFUL_ENVIRONMENT_NAME,
    accessToken: process.env.GATSBY_REALTIME_CONTENTFUL_ACCESS_TOKEN,
  },
  firebase: {
    key: process.env.GATSBY_FIREBASE_API_KEY,
    databaseURL: process.env.GATSBY_FIREBASE_DATABASE_URL,
    projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
    appId: process.env.GATSBY_FIREBASE_APP_ID,
  },
  cognito: {
    region: baseCognito.region,
    userPoolId: baseCognito.userPoolId,
    userPoolWebClientId: baseCognito.userPoolWebClientId,
    authenticationFlowType: baseCognito.authenticationFlowType,
    installerPortal: {
      region:
        process.env.GATSBY_COGNITO_INSTALLER_PORTAL_REGION ||
        baseCognito.region,
      userPoolId:
        process.env.GATSBY_COGNITO_INSTALLER_PORTAL_USER_POOL_ID ||
        baseCognito.userPoolId,
      userPoolWebClientId:
        process.env.GATSBY_COGNITO_INSTALLER_PORTAL_USER_POOL_WEB_CLIENT_ID ||
        baseCognito.userPoolWebClientId,
      authenticationFlowType:
        process.env.GATSBY_COGNITO_INSTALLER_PORTAL_AUTH_FLOW_TYPE ||
        baseCognito.authenticationFlowType,
    },
    invoicePortal: {
      region:
        process.env.GATSBY_COGNITO_INVOICE_PORTAL_REGION || baseCognito.region,
      userPoolId:
        process.env.GATSBY_COGNITO_INVOICE_PORTAL_USER_POOL_ID ||
        baseCognito.userPoolId,
      userPoolWebClientId:
        process.env.GATSBY_COGNITO_INVOICE_PORTAL_USER_POOL_WEB_CLIENT_ID ||
        baseCognito.userPoolWebClientId,
      authenticationFlowType:
        process.env.GATSBY_COGNITO_INVOICE_PORTAL_AUTH_FLOW_TYPE ||
        baseCognito.authenticationFlowType,
    },
  },
  legacySiteURL: process.env.GATSBY_LEGACY_URL_SITE,
  bizWebLegacyURL: process.env.GATSBY_LEGACY_URL_BIZ_WEB,
  bizApiLegacyURL: process.env.GATSBY_LEGACY_URL_BIZ_API,
  csApiLegacyURL: process.env.GATSBY_LEGACY_URL_CS_API,
  salseforceEVFormURL: process.env.GATSBY_SALESFORCE_EV_FORM_URL,
  amazonLocationService: {
    key: process.env.GATSBY_AMAZON_LOCATION_SERVICE_KEY,
    region: process.env.GATSBY_AMAZON_LOCATION_SERVICE_REGION,
    arcgisPSPSURL: process.env.GATSBY_ARCGIS_PSPS_API_URL,
  },
};

const config = ConfigSchema.parse(rawConfig);

export default config;
